import { ImageName } from "@mcleod/images";
import { ActionPermission, Api, Currency, CurrencySettings, CurrencyUtil, DisplayType, HorizontalAlignment, PermissionsUtil, StringUtil } from "@mcleod/core";
import { BlurEvent, Button, ButtonVariant, ClickEvent, DataSource, DataSourceExecutionEvent, DataSourceMode, Layout, Panel, PanelProps, Table, TableColumn, Textbox, TextboxProps } from "@mcleod/components";
import { ExchangeRateQuickInfo, getDispatchControlBoolean } from "@mcleod/general";
import { RowMovement } from "@mcleod/dispatch";
import { AutogenLayoutMovementCarrierPay } from "./autogen/AutogenLayoutMovementCarrierPay";

export function userCanOverrideMaxPay(fromBilling: boolean = false): boolean {
    if (fromBilling) {
        return false;
    }
    return !getDispatchControlBoolean("enforce_max_pay") || !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MAX_PAY_OVERRIDE)
}

export function userCanOverrideTargetPay(fromBilling: boolean = false): boolean {
    if (fromBilling) {
        return false;
    }
    return !getDispatchControlBoolean("enforce_target_pay") || !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_TARGET_PAY_OVERRIDE)
}

export class MovementCarrierPay extends AutogenLayoutMovementCarrierPay {
    private fromBilling: boolean = false;
    private orderSource: DataSource;
    override onLoad(): void {
        this.setTooltipContent(this.textboxMaxPayCalc);
        this.layoutTargetPay.addLayoutLoadListener(() => {
            this.setTooltipContent(this.layoutTargetPay.textboxTargetPayCalc);
            this.layoutTargetPay.textboxTargetPayCalc.addBlurListener(event => this.payTextboxOnBlur(event));
        })
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.setupOverrideCurrencyBlurListeners();
        }
    }

    private setTooltipContent(textbox: Textbox) {
        textbox.tooltipDynamicContent = () => {
            if (textbox.printable) {
                return this.makeTargetMaxPayTooltip(textbox);
            }
            return null;
        }
    }

    configureForOrderEntry(orderSource: DataSource, fromBilling: boolean = false) {
        this.orderSource = orderSource;
        this.remove(this.panelOrderCharges);

        if (orderSource?.activeRow?._appending) {
            this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [new RowMovement()]);
        } else if (this.orderSource?.activeRow?.get("curr_movement_id") != null) {
            this.mainDataSource.search({ id: this.orderSource?.activeRow?.get("curr_movement_id") }).then(() => {
                this.mainDataSource.mode = DataSourceMode.UPDATE;
            })
        }
        const panelProps: Partial<PanelProps> = { borderRightWidth: 0, width: '33%' }
        this.panelTargetPay.setProps({ ...panelProps });
        this.panelMaxPay.setProps({ ...panelProps });
        this.textboxMaxPayCalc.setProps({ marginLeft: 5 });
        this.panelOrderCharges.setProps({ ...panelProps });
        this.fromBilling = fromBilling;
    }

    /** This is an event handler for the afterModeChange event of sourceMovement.  */
    sourceMovementAfterModeChange(event) {
        this.buttonOverrideMaxPay.enabled = userCanOverrideMaxPay(this.fromBilling);
        this.buttonOverrideTargetPay.enabled = userCanOverrideTargetPay(this.fromBilling);
    }

    sourceMovementAfterExecution(event) {
        const move = this.sourceMovement.activeRow;
        const status = move.getString("status");
        const showBtm = getDispatchControlBoolean("show_btm", false);
        this.panelBuyToMarket.visible = showBtm && StringUtil.equalsIgnoreCase("A", status);
    }

    public async displayCalculatedFields() {
        Api.search("lme/dispatch/calculate-override-pay", {
            "movement_id": this.mainDataSource.activeRow.get("id"),
            "order_id": this.mainDataSource.activeRow?.get("orders.id"),
            "pay_method": this.mainDataSource.activeRow.get("override_type"),
            "rate_per_unit": this.mainDataSource.activeRow.get("override_pay_rate")
        }).then(response => {
            this.displayCalcPromiseResult(response);
        })
    }

    displayCalcPromiseResult(response: any) {
        this.displayPayFields(response.data[0]);
    }

    makeTargetMaxPayTooltip(textbox: Textbox): Panel {
        const maxPay = textbox.id === "textboxMaxPayCalc";
        const dataSource = textbox.dataSource;
        const row = dataSource.activeRow.createBasicCopy();
        const methodCaption = maxPay ? "Max Pay Method" : "Target Pay Method";
        const methodText = getPayMethodDescription(row.get(maxPay ? "max_pay_method" : "target_pay_method"));
        const amountCurrency = row?.getCurrency(maxPay ? "max_buy" : "target_pay")?.getCurrencyCode();
        const perMileAmount: number | Currency = row?.get(maxPay ? "max_per_mile" : "target_per_mile");
        const targetRateId: number = row.getNumber("target_rate_id", null);
        if (amountCurrency && typeof perMileAmount == "number") {
            row.set(maxPay ? "max_per_mile" : "target_per_mile", new Currency({ amount: perMileAmount, currency_code: amountCurrency }));
        } else if (perMileAmount instanceof Currency) {
            row.set(maxPay ? "max_per_mile" : "target_per_mile", perMileAmount);
        } else {
            row.set(maxPay ? "max_per_mile" : "target_per_mile", null);
        }
        const textProps: Partial<TextboxProps> = { printable: true, nullDisplayValue: "--" }
        let payFields: Partial<TextboxProps>[] = [
            { caption: "Per Mile", field: maxPay ? "max_per_mile" : "target_per_mile" },
            { caption: "Amount", field: maxPay ? "max_buy" : "target_pay" },
            { caption: "Override Amount", field: maxPay ? "override_max_pay" : "override_targetpay" }
        ];

        const targetRateData = row.get("target_rate_data", "");
        if (!maxPay && row.get("target_pay_method", "") === "S" && !StringUtil.isEmptyString(targetRateData)) {
            const mpactRateData = JSON.parse(targetRateData);
            const mpactRateFields = [
                { caption: "Number of Orders", text: mpactRateData.sourceInfo.orders },
                { caption: "Number of Contributors", text: mpactRateData.sourceInfo.contributors },
                { caption: "Days", text: mpactRateData.sourceInfo.days }
            ];
            payFields = payFields.concat(mpactRateFields);
        }

        const panelTooltip = new Panel({id: "panelTooltip"});
        if (targetRateId) {
            const textboxTargetRateId = new Textbox({
                ...textProps,
                id: "textboxTargetRateId",
                displayType: DisplayType.STRING,
                caption: "Target Rate ID",
                text: targetRateId.toString(),
                fillRow: true,
                rowBreak: false
            });
            const buttonLinkTargetRate = new Button({
                color: "primary",
                id: "buttonOpenTargetRate",
                imageName: ImageName.detail,
                link: `lme/dispatch/rates/CarrierTargetRate?mode\u003dupdate\u0026key\u003d${targetRateId}`,
                variant: ButtonVariant.round
            })
            panelTooltip.add(textboxTargetRateId, buttonLinkTargetRate);
        }

        const panelMain = new Panel({
            id: "panelMain", components: [
                new Textbox({ ...textProps, displayType: DisplayType.STRING, caption: methodCaption, text: methodText }),
                ...this.createTooltipTextboxes(textProps, payFields)
            ]
        });
        panelTooltip.add(panelMain);

        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const exchangeLayout = Layout.getLayout("lme/general/multicurrency/ExchangeRateQuickInfo") as ExchangeRateQuickInfo;
            let mainCurrency;
            const perMileDistance = row?.get("move_distance");
            if (row.isNull(maxPay ? "override_max_pay" : "override_targetpay")) {
                mainCurrency = row.getCurrency(maxPay ? "max_buy" : "target_pay");
            }
            else {
                mainCurrency = row.getCurrency(maxPay ? "override_max_pay" : "override_targetpay");
            }
            exchangeLayout.addLayoutLoadListener(() => {
                const tableExchangeRates: Table = exchangeLayout.tableExchangeRates;
                tableExchangeRates.addColumn(new TableColumn({
                    headingDef: { caption: "Per mile", align: HorizontalAlignment.RIGHT },
                    cell: { field: "per_mile", fillRow: true, align: HorizontalAlignment.RIGHT, paddingRight: 12 }
                }), false, true);
                const numOfColumns = tableExchangeRates.columns.length;
                tableExchangeRates.moveColumn(numOfColumns - 1, 1);
                tableExchangeRates.columns[numOfColumns - 1].headingCell.paddingRight = 0;
                tableExchangeRates.width = 500;
                exchangeLayout.sourceCurrencyConversions?.search({ currency_for_exchange: mainCurrency, move_distance: perMileDistance });
            });
            panelMain.setProps({ width: 150, rowBreak: false });
            panelTooltip.add(panelMain, exchangeLayout);
        } else {
            panelTooltip.setProps({ width: 150});
            panelTooltip.add(panelMain);
        }

        panelTooltip.displayData(row, null, 0);
        return panelTooltip;
    }

    createTooltipTextboxes(textProps: Partial<TextboxProps>, payFields: Partial<TextboxProps>[]): Textbox[] {
        return payFields.map(payField => {
            return new Textbox({ ...textProps, displayType: payField.field ? DisplayType.CURRENCY : DisplayType.STRING, caption: payField.caption, field: payField.field ?? null, text: payField.text ?? null });
        })
    }

    private displayPayFields(data) {
        this.mainDataSource.activeRow?.set("orders.freight_charge", data["order_freight_charge"]);
        this.mainDataSource.activeRow?.set("orders.otherchargetotal", data["order_other_charges"]);
        this.mainDataSource.activeRow?.set("orders.total_charge", data["order_total_charges"]);
        this.mainDataSource.activeRow?.set("buy_to_market", data["buy_to_market"]);
        this.mainDataSource.activeRow?.set("buy_to_market_percent", data["buy_to_market_percent"]);
    }

    buttonOverrideMaxPayOnClick(event: ClickEvent) {
        this.overrideButtonOnClick(this.textboxMaxPayCalc, "override_max_pay")
    }
    buttonOverrideTargetPayOnClick(event: ClickEvent) {
        this.overrideButtonOnClick(this.layoutTargetPay.textboxTargetPayCalc, "override_targetpay")
    }

    overrideButtonOnClick(textbox: Textbox, field: string) {
        textbox.width = 110;
        textbox.printable = false;
        if (textbox.field != field) {
            this.activeRow?.set(field, this.activeRow.getCurrency(textbox.field));
            textbox.field = field;
        }
        textbox.focus();
        textbox.selectText();
    }

    payTextboxOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const textbox = event.target as Textbox;
        textbox.width = undefined;
        textbox.printable = true;
        this.validateOverride(textbox)
        this.calculateMargins(textbox);
        if (this.orderSource != null) {
            this.updateOrderSource(textbox);
        }
    }

    updateOrderSource(textbox: Textbox) {
        const payFields = this.getPayTextboxFields(textbox);
        if (payFields.overrideField == textbox.field) {
            const originalAmt = this.activeRow?.originalData?.[textbox.field + "_n"];
            const newAmt = this.activeRow.get(textbox.field)?.amount;
            if (newAmt != originalAmt)
                this.orderSource.activeRow.set(textbox.field, this.activeRow?.get(textbox.field));
            else
                this.orderSource.activeRow.set(textbox.field, undefined);
        }
    }

    sourceMovementAfterExecute(event: DataSourceExecutionEvent) {
        if (event.data) {
            this.layoutTargetPay.setupMpact(event.data);
            const status = event.data.getString("status");
            const showBtm = getDispatchControlBoolean("show_btm", false);
            this.panelBuyToMarket.visible = showBtm && StringUtil.equalsIgnoreCase("A", status);
        }
    }

    // checks if override amount is not undefined and differs from pay amount
    validateOverride(textbox: Textbox) {
        const payFields = this.getPayTextboxFields(textbox);
        const calcValue = this.activeRow?.getCurrency(payFields.payField);
        const overrideValue = this.activeRow?.getCurrency(payFields.overrideField);
        if (overrideValue == undefined || CurrencyUtil.currenciesAreEqual(overrideValue, calcValue)) {
            textbox.field = payFields.payField;
            this.activeRow.set(payFields.overrideField, null);
        }
        const overrideNull = this.activeRow.isNull(payFields.overrideField);
        this.layoutTargetPay.mpactRatingEnabled(overrideNull);
    }

    async calculateMargins(textbox: Textbox) {
        const payFields = this.getPayTextboxFields(textbox);
        let totalCharge: Currency = null;
        if (this.orderSource != null) {
            totalCharge = this.orderSource.activeRow?.getCurrency("total_charge");
        } else {
            totalCharge = this.activeRow?.getCurrency("orders.total_charge");
        }
        textbox.printable = true;
        const value = this.activeRow?.getCurrency(textbox.field);
        const distance = this.activeRow?.get("move_distance");
        this.activeRow.set({
            [payFields.marginField]: new Currency({ amount: 0.0, currency_code: totalCharge?.getCurrencyCode(), conversion_date: totalCharge?.getConversionDate() }),
            [payFields.perMileField]: new Currency({ amount: 0.0, currency_code: totalCharge?.getCurrencyCode(), conversion_date: totalCharge?.getConversionDate() }),
            [payFields.marginPctField]: null
        });
        if (totalCharge?.getAmount() != null && value?.getAmount() != null) {
            const totalChargeClone = totalCharge.getClone(totalCharge.getAmount());
            const marginValue = await totalChargeClone.subtract(value);
            const margin = new Currency({ amount: marginValue, currency_code: totalCharge?.getCurrencyCode(), conversion_date: totalCharge?.getConversionDate() });
            this.activeRow.set(payFields.marginField, margin);
            if (totalCharge?.getAmount() != 0) {
                this.activeRow.set(payFields.marginPctField, ((margin?.getAmount() / totalCharge?.getAmount()) * 100).toFixed(2) + "%");
            }

            if (distance != null && distance != 0) {
                const perMileCurrency = new Currency({ amount: (value?.getAmount() / distance), currency_code: totalCharge?.getCurrencyCode(),
                    conversion_date: totalCharge?.getConversionDate() });
                this.activeRow.set(payFields.perMileField, perMileCurrency);
            }
        }
    }

    // code below is for calculating / overriding max and target pay from Orders page
    buttonCalcMaxTargetOnClick(event: ClickEvent) {
        if (this.orderSource == null) return;
        const data = this.orderSource.getDataboundValues(null, true, null, null);
        data.removeLookupModelData();
        data.set("total_charge", this.orderSource.activeRow.get("total_charge"));
        return Api.search("lme/dispatch/calculate-order-max-target", { order_row: data }).then(response => {
            this.setDataFromResponse(response.data[0], true);
        });
    }

    setDataFromResponse(responseData: any, clearData?: boolean) {
        this.activeRow.set("move_distance", responseData?.move_distance);
        this.activeRow.set("target_rate_data", responseData?.target_rate_data);
        this.setPayDataFromResponse(responseData, this.layoutTargetPay.textboxTargetPayCalc, clearData);
        this.setPayDataFromResponse(responseData, this.textboxMaxPayCalc, clearData);
    }

    setPayDataFromResponse(responseData: any, textbox: Textbox, clearData: boolean) {
        const payFields = this.getPayTextboxFields(textbox);
        if (clearData || this.activeRow.get(payFields.overrideField) == null) {
            payFields.allFields.forEach(field => this.activeRow.set(field, null));
            textbox.field = payFields.payField;
            payFields.allFields.forEach(field => this.activeRow.set(field, responseData?.[field]));
        }
        textbox.parent.displayData(this.activeRow, null, 0);
    }

    getPayTextboxFields(textbox: Textbox): PayTextboxFields {
        if (textbox == this.textboxMaxPayCalc) {
            return {
                isMaxPay: true, payField: "max_buy", overrideField: "override_max_pay",
                marginField: "max_margin", marginPctField: "max_margin_percent",
                perMileField: "max_per_mile",
                allFields: ["max_buy", "override_max_pay", "max_pay_calc", "max_margin", "max_margin_percent", "max_per_mile", "max_pay_method"]
            }
        }
        else {
            return {
                isMaxPay: false, payField: "target_pay", overrideField: "override_targetpay",
                marginField: "target_margin", marginPctField: "target_margin_percent",
                perMileField: "target_per_mile",
                allFields: ["target_pay", "override_targetpay", "target_pay_calc", "target_margin", "target_margin_percent", "target_per_mile", "target_pay_method"]
            }
        }
    }

    private setupOverrideCurrencyBlurListeners() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;

        this.textboxMaxPayCalc.addBlurListener(() => {
            let conversionDate: Date;
            if (this.orderSource) {
                conversionDate = this.orderSource.activeRow.getCurrency("freight_charge", new Currency(0.0))?.getConversionDate();
            }
            else {
                conversionDate = this.sourceMovement.activeRow.getCurrency("orders.freight_charge", new Currency(0.0))?.getConversionDate();
            }
            this.setConversionDateForComponent(this.textboxMaxPayCalc, conversionDate);
        });
        this.layoutTargetPay.textboxTargetPayCalc.addBlurListener(() => {
            let conversionDate: Date;
            if (this.orderSource) {
                conversionDate = this.orderSource.activeRow.getCurrency("freight_charge", new Currency(0.0))?.getConversionDate();
            }
            else {
                conversionDate = this.sourceMovement.activeRow.getCurrency("orders.freight_charge", new Currency(0.0))?.getConversionDate();
            }
            this.setConversionDateForComponent(this.layoutTargetPay.textboxTargetPayCalc, conversionDate);
        });
    }

    validateCarrierPayOverrideDates() {
        let conversionDate: Date;
        if (this.orderSource) {
            conversionDate = this.orderSource.activeRow.getCurrency("freight_charge", new Currency(0.0))?.getConversionDate();
        }
        else {
            conversionDate = this.sourceMovement.activeRow.getCurrency("orders.freight_charge", new Currency(0.0))?.getConversionDate();
        }

        this.setConversionDateForComponent(this.layoutTargetPay.textboxTargetPayCalc, conversionDate);
        this.setConversionDateForComponent(this.textboxTargetMarginAmt, conversionDate);
        this.setConversionDateForComponent(this.textboxMaxPayCalc, conversionDate);
        this.setConversionDateForComponent(this.textboxMaxPayMarginAmt, conversionDate);
    }

    private setConversionDateForComponent(textbox: Textbox, conversionDate: Date) {
        const currency = this.sourceMovement.activeRow.getCurrency(textbox.field);
        if (currency && conversionDate) {
            currency.setConversionDate(conversionDate);
            this.sourceMovement.activeRow.set(textbox.field, currency);
            this.sourceMovement.displayDataInBoundComponent(textbox);
        }
    }
}

interface PayTextboxFields {
    allFields: string[], isMaxPay: boolean, payField: string, overrideField: string,
    marginField: string, marginPctField: string, perMileField: string
}

const methods = {
    "F": "Flat", "D": "Distance", "P": "Margin %", "I": "Market Rate - ITS", "B": "Market Rate - DAT",
    "M": "Market Rate - McLeod", "N": "None", "G": "Pending - DAT", "T": "Pending - DAT", "E": "Pending - McLeod", "": "", "S": "MPACT - Smart Target"
};

function getPayMethodDescription(method: string): string {
    return methods[method];
}
